import { SearchOutlined } from "@ant-design/icons";
import { Badge, Typography } from "antd";
import Tags from "screens/designStudio/shared/Tags";
import NameCardCell from "shared/components/NameCardCell";
import { SearchFilterDropdown } from "shared/components/SearchFilterDropdown";
import { TimeAgo } from "shared/components/TimeAgo";
import { ITableColumnURL } from "shared/components/dataListURL/DataListURLTable";
import { ArchiveTableItem } from "shared/types/salesEnablementArchive";
import { deliveryMethods } from "screens/designStudio/utils";
import styles from "./columns.module.scss";

enum Status {
  COMPLETE = "COMPLETE",
  IN_PROGRESS = "IN_PROGRESS",
}

export const StatusMap = {
  [Status.COMPLETE]: {
    color: "green",
    text: "Complete",
  },
  [Status.IN_PROGRESS]: {
    color: "blue",
    text: "In Progress",
  },
} satisfies Record<Status, { color: string; text: string }>;

export function getColumns({
  tableType,
  nested,
  isSalesEnablementAdmin,
  onMaterialClick,
  onTemplateClick,
}: {
  tableType: "historyLog" | "templates";
  nested?: boolean;
  isSalesEnablementAdmin?: boolean;
  onMaterialClick?: (record: ArchiveTableItem) => void;
  onTemplateClick?: (record: ArchiveTableItem) => void;
}): (ITableColumnURL<keyof ArchiveTableItem, ArchiveTableItem> & {
  hidden?: boolean;
})[] {
  const cols: (ITableColumnURL<
    keyof Partial<ArchiveTableItem>,
    ArchiveTableItem
  > & { hidden?: boolean })[] = [
    {
      title:
        tableType === "historyLog"
          ? "Marketing Material Name"
          : "Source Template / Marketing Material",
      dataIndex: "marketingMaterialName",
      key: "marketingMaterialName",
      width: nested ? 275 : 300,
      render: (_, record) => {
        const isMixed =
          record.deliveryMethods?.includes("print") &&
          !record.marketingMaterialName;
        if (isMixed)
          return (
            <Typography.Text type="secondary" className={styles.mixed}>
              Mixed
            </Typography.Text>
          );

        return (
          <NameCardCell
            thumbnail={getThumbnail(record)}
            name={record.marketingMaterialName}
            onCellClick={() =>
              nested || tableType == "historyLog"
                ? onMaterialClick?.(record)
                : onTemplateClick?.(record)
            }
          />
        );
      },
      filterDropdown: SearchFilterDropdown,
      filterIcon: <SearchOutlined />,
    },
    {
      title: "Deliveries",
      dataIndex: "deliveries",
      key: "deliveries",
      render: (_, record) => (nested ? "" : record.deliveries),
      width: 100,
    },
    {
      title: "Delivery Method",
      dataIndex: "deliveryMethod",
      key: "deliveryMethod",
      width: 150,
      render: (_, record) => {
        if (!record.deliveryMethods && !record.deliveryMethod) return null;
        return (
          <Tags
            tags={
              tableType === "historyLog" || nested
                ? [
                    {
                      id: record.deliveryMethod!,
                      name:
                        deliveryMethods.find(
                          opt => opt.value === record.deliveryMethod,
                        )?.label || record.deliveryMethod!,
                    },
                  ]
                : record.deliveryMethods?.map(method => ({
                    id: method,
                    name:
                      deliveryMethods.find(opt => opt.value === method)
                        ?.label || method,
                  })) ?? []
            }
          />
        );
      },
      filterDropdown: SearchFilterDropdown,
      filterIcon: <SearchOutlined />,
    },
    {
      title: "Order Status",
      dataIndex: "orderStatus",
      key: "orderStatus",
      width: 150,
      hidden: !isSalesEnablementAdmin,
      render: (_, record) => {
        if (record.deliveryMethod !== "print") return;
        const { orderStatus } = record;
        if (!orderStatus) return null;
        const { color, text } = StatusMap[orderStatus];
        return (
          <span>
            <Badge color={color} />
            {text}
          </span>
        );
      },
      filterDropdown: SearchFilterDropdown,
      filterIcon: <SearchOutlined />,
    },
    {
      title: "Order ID",
      dataIndex: "id",
      key: "id",
      width: 150,
      render: (_, record) => {
        if (nested || tableType == "historyLog") return record.id;
      },
      filterDropdown: SearchFilterDropdown,
      filterIcon: <SearchOutlined />,
    },
    {
      title: "Est. Amount",
      dataIndex: "estimatedAmount",
      key: "estimatedAmount",
      width: 150,
      render: (_, record) => record.estimatedAmount ?? "",
      filterDropdown: SearchFilterDropdown,
      filterIcon: <SearchOutlined />,
      hidden: !isSalesEnablementAdmin,
    },
    {
      title: "Source Template",
      dataIndex: "sourceTemplate",
      key: "templateName",
      render: (_, record) => (
        <a onClick={() => onTemplateClick?.(record)}>{record.templateName}</a>
      ),
      filterDropdown: SearchFilterDropdown,
      filterIcon: <SearchOutlined />,
      width: 250,
    },
    {
      title: "Account",
      dataIndex: "account",
      key: "account",
      width: 150,
      filterDropdown: SearchFilterDropdown,
      filterIcon: <SearchOutlined />,
    },
    {
      title: "Products",
      dataIndex: "products",
      key: "products",
      render: (_, { products, id }) => (
        <Tags
          tags={
            products?.map(product => ({
              id: product + id,
              name: product,
            })) ?? []
          }
        />
      ),
      width: 300,
      filterDropdown: SearchFilterDropdown,
      filterIcon: <SearchOutlined />,
    },
    {
      title: "States",
      dataIndex: "locations",
      key: "locations",
      render: (_, { locations, id }) => (
        <Tags
          tags={
            locations?.map(product => ({
              id: product + id,
              name: product,
            })) ?? []
          }
        />
      ),
      width: 200,
      filterDropdown: SearchFilterDropdown,
      filterIcon: <SearchOutlined />,
    },
    {
      title: "Audience",
      dataIndex: "audience",
      key: "audience",
      render: (_, { audience, marketingMaterial }) => (
        <Tags
          tags={
            audience?.map(col => ({
              id: col + marketingMaterial?.id,
              name: col,
            })) ?? []
          }
        />
      ),
      width: 200,
      filterDropdown: SearchFilterDropdown,
      filterIcon: <SearchOutlined />,
    },
    {
      title: "Coll. Type",
      dataIndex: "colType",
      key: "colType",
      render: (_, { colType }) => (
        <Tags
          tags={
            colType?.map(col => ({
              id: col,
              name: col,
            })) ?? []
          }
        />
      ),
      width: 150,
      filterDropdown: SearchFilterDropdown,
      filterIcon: <SearchOutlined />,
    },
    {
      title: "Language",
      dataIndex: "language",
      key: "language",
      width: 100,
      filterDropdown: SearchFilterDropdown,
      filterIcon: <SearchOutlined />,
      render: (_, record) => (record.language == "es" ? "Spanish" : "English"),
    },
  ];

  if (isSalesEnablementAdmin) {
    const agentCols: ITableColumnURL<
      keyof Partial<ArchiveTableItem>,
      ArchiveTableItem
    >[] = [
      {
        title: "Agent",
        dataIndex: "agent",
        key: "agent",
        width: 250,
        filterDropdown: SearchFilterDropdown,
        filterIcon: <SearchOutlined />,
      },
      {
        title: "Agent ID",
        dataIndex: "agentId",
        key: "agentId",
        width: 300,
        filterDropdown: SearchFilterDropdown,
        filterIcon: <SearchOutlined />,
      },
    ];
    cols.splice(5, 0, ...agentCols);
  }
  const filteredColumns = cols.filter(col => !col.hidden);

  return tableType == "historyLog"
    ? [...[deliveryDateColumn]].concat(
        filteredColumns.filter(col => col.key !== "deliveries"),
      )
    : filteredColumns
        .concat(deliveryDateColumn)
        .filter(col => col.key !== "templateName");
}

const deliveryDateColumn: ITableColumnURL<
  keyof ArchiveTableItem,
  ArchiveTableItem
> = {
  title: "Date of Order",
  dataIndex: "createdAt",
  key: "createdAt",
  width: 150,
  defaultSortOrder: "descend",
  render: (_, record) => {
    return <TimeAgo date={record.createdAt ?? ""} />;
  },
};

const getThumbnail = (record: ArchiveTableItem) => {
  const isChildArchive = record.archives?.length;
  if (isChildArchive) {
    return record.archives?.[0]?.marketingMaterial?.templateThumbnail;
  }
  return record.marketingMaterial?.templateThumbnail;
};
